import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';

export default function AuthCallback() {
  const { user } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const mountedRef = useRef(true);
  const redirectingRef = useRef(false);

  const redirectHome = (delayMs: number) => {
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    setTimeout(() => {
      window.location.replace('/');
    }, delayMs);
  };

  useEffect(() => {
    mountedRef.current = true;
    redirectingRef.current = false;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    // Check for OAuth error params in URL first
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const searchParams = new URLSearchParams(window.location.search);
    const oauthError = hashParams.get('error') || searchParams.get('error');
    const oauthErrorDescription = hashParams.get('error_description') || searchParams.get('error_description');

    if (oauthError) {
      const rawDescription = oauthErrorDescription || oauthError;
      const lowerDesc = rawDescription.toLowerCase();
      const isDatabaseUserError =
        lowerDesc.includes('database error saving new user') ||
        lowerDesc.includes('database error creating new user');
      setErrorMessage(
        isDatabaseUserError
          ? 'WatchWicket is currently in private beta. A valid invitation is required to create an account.'
          : rawDescription
      );
      setStatus('error');
      redirectHome(3000);
      return;
    }

    // Use onAuthStateChange to respond to the session established by
    // Supabase's detectSessionInUrl processing. This avoids calling
    // getSession() which contends for the SDK's internal navigator lock
    // while detectSessionInUrl is still running.
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled || !mountedRef.current || redirectingRef.current) return;

      if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN') && session) {
        setStatus('success');
        redirectHome(800);
      }
    });

    // Hard timeout: if nothing happens in 15s, show error
    timeoutId = setTimeout(() => {
      if (cancelled || !mountedRef.current || redirectingRef.current) return;
      setStatus('error');
      setErrorMessage('Sign-in is taking longer than expected. Please try again.');
      redirectHome(3000);
    }, 15000);

    return () => {
      cancelled = true;
      mountedRef.current = false;
      authSubscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  // Fallback: if the onAuthStateChange listener missed the SIGNED_IN event
  // (race with detectSessionInUrl's setTimeout(0) dispatch), AuthContext's
  // own listener will still have received it and set `user`. React to that
  // state change here without calling getSession() or getUser().
  useEffect(() => {
    if (!mountedRef.current || redirectingRef.current) return;
    if (user) {
      setStatus('success');
      redirectHome(800);
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        {status === 'loading' && (
          <>
            <div className="mb-6">
              <div className="inline-block w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <h1 className="text-2xl font-bold mb-2">Completing Sign In</h1>
            <p className="text-slate-400">Please wait while we sign you in...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mb-6 text-6xl">✓</div>
            <h1 className="text-2xl font-bold mb-2 text-green-400">Success!</h1>
            <p className="text-slate-400">Redirecting you to the app...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mb-6 text-6xl">⚠️</div>
            <h1 className="text-2xl font-bold mb-2 text-red-400">Authentication Error</h1>
            <p className="text-slate-300 mb-4">{errorMessage}</p>
            <p className="text-slate-400 text-sm">Redirecting you back...</p>
          </>
        )}
      </div>
    </div>
  );
}
